<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { dryingAt, dryingFromTime, dryingTimeline, levelAt, type DryingOut } from '@quicktide/shared'
import { useAppStore } from '../store'
import { addDays, shiftPoints } from '../lib/tide'
import { useDerivedSeries, useDerivedActuals, bestCurve } from '../lib/series'
import TideChart from '../components/TideChart.vue'
import DateStrip from '../components/DateStrip.vue'
import InfoTip from '../components/InfoTip.vue'
import MeasureStepper from '../components/MeasureStepper.vue'
import PlaceTabs from '../components/PlaceTabs.vue'
import PlaceTitle from '../components/PlaceTitle.vue'

const store = useAppStore()
const now = ref(Date.now())
let timer: number | undefined
onMounted(() => { timer = window.setInterval(() => { now.value = Date.now() }, 30_000) })
onUnmounted(() => window.clearInterval(timer))

const dayEnd = computed(() => addDays(store.selectedDay, 1))
const { points: astro } = useDerivedSeries(() => ({ from: store.selectedDay - 86_400_000, to: dayEnd.value + 86_400_000 }))
const { observed, forecast } = useDerivedActuals()

/**
 * How much later the tide runs here than the curve says. Local knowledge: a spot up a creek
 * fills and empties after the gauge does, and half an hour decides a dry-out.
 */
const tideShift = ref(0)

/** Plan against the skipper's own rise or fall, else what is expected where that is known, and astronomy beyond it */
const best = computed(() => bestCurve(astro.value, observed.value, forecast.value, store.settings.surge))
const points = computed(() => (tideShift.value ? shiftPoints(best.value, tideShift.value * 60_000) : best.value))

/**
 * The one thing the skipper chooses: when to take the ground. Everything else follows -- the
 * spot is as deep as the water is at that moment, less the draught, so grounding later means
 * a higher spot and a longer dry.
 */
const groundingMinutes = ref(12 * 60)
/**
 * The other way in: the depth under the boat right now, off the sounder. That pins the seabed
 * where it actually is, and the moment of grounding becomes the answer rather than the
 * question. Whichever of the two was touched last is the one that drives.
 */
const currentDepth = ref(0)
type Mode = 'time' | 'depth'
const mode = ref<Mode>('time')
const touched = ref(false)
const groundingTime = computed(() => store.selectedDay + groundingMinutes.value * 60_000)

/**
 * Open at the clock, as the old app does. Hunting for the next falling tide sounds helpful and
 * is not: it lands on the top of the highest water of the day, which is the one moment that
 * strands you for thirteen hours.
 */
watch(() => store.selectedDay, () => {
  if (touched.value) return
  groundingMinutes.value = Math.max(0, Math.min(24 * 60 - 1, Math.round((Date.now() - store.selectedDay) / 60_000)))
}, { immediate: true })

const result = computed<DryingOut | null>(() => {
  if (!points.value.length) return null
  if (mode.value === 'depth') {
    const levelNow = levelAt(points.value, now.value)
    if (levelNow === null) return null
    // the sounder reads the whole water column, so the seabed is that much below the surface
    return dryingAt(points.value, levelNow - currentDepth.value, store.settings.draught, now.value)
  }
  return dryingFromTime(points.value, groundingTime.value, store.settings.draught)
})

/** In depth mode the moment of grounding is an outcome, so show the one that was worked out */
const groundingShown = computed(() =>
  mode.value === 'depth' && result.value?.aground ? result.value.aground.from : groundingTime.value)

const phases = computed(() => {
  const r = result.value
  if (!r) return []
  // the whole day, not just the episode around the chosen moment: a spot that dries at
  // one low water dries again at the next. Afloat is the ordinary state and gets no bar:
  // the bars are there to make the two that matter stand out.
  return dryingTimeline(points.value, r.seabed, store.settings.draught, store.selectedDay, dayEnd.value)
    .filter(p => p.state !== 'afloat')
})

/** The boat sits on the spot it grounds on, which is where the water is when it touches */
const boatLevel = computed(() => {
  const r = result.value
  if (!r) return levelAt(points.value, groundingShown.value)
  return r.seabed + store.settings.draught
})

/** Dragging the boat along the chart is the same as choosing the moment of grounding */
function setGroundingTime(t: number) {
  touched.value = true
  mode.value = 'time'
  groundingMinutes.value = Math.max(0, Math.min(24 * 60 - 1, Math.round((t - store.selectedDay) / 60_000)))
}
function stepGrounding(by: number) {
  touched.value = true
  mode.value = 'time'
  groundingMinutes.value = Math.max(0, Math.min(24 * 60 - 1, groundingMinutes.value + by))
}
function setDepth(cm: number) {
  touched.value = true
  mode.value = 'depth'
  currentDepth.value = cm
}
/** The old app's range: two hours either way, in five-minute steps */
function stepTideShift(by: number) {
  tideShift.value = Math.max(-120, Math.min(120, tideShift.value + by))
}

/**
 * A measured water depth and the level at that moment give the height of the ground, which
 * is exactly what a sounding is. The catalogs count depth downwards from NAP, so the sign
 * turns over on the way in.
 */
function keepSounding() {
  const r = result.value
  if (!r || !store.shallow) return
  store.setOwnDepth(-r.seabed)
}

function fmtTime(t: number) {
  return new Date(t).toLocaleTimeString(store.settings.locale, { hour: '2-digit', minute: '2-digit' })
}
function fmtDuration(ms: number) {
  const m = Math.round(ms / 60_000)
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`
}
function fmtMetres(cm: number) {
  return `${(cm / 100).toFixed(2)} m`
}
const bandLabel = (s: string) => store.t(s === 'dry' ? 'stateDry' : 'stateAground')
</script>

<template>
  <main class="screen">
    <div class="header">
      <PlaceTitle />
      <PlaceTabs />
    </div>

    <template v-if="result && result.aground">
      <div class="bigline">
        <span class="level num">{{ fmtDuration(result.dryMs) }}</span>
        <span class="unit">{{ store.t('hoursDry') }}</span>
        <span class="source">
          {{ store.t('aground') }} {{ fmtTime(result.aground.from) }}–{{ fmtTime(result.aground.to) }}
          <span class="dev num"> · {{ store.t('seabedAt') }} {{ store.displayLevel(result.seabed).toFixed(2) }} m {{ store.refLevelName }}</span>
        </span>
      </div>
    </template>
    <p v-else-if="!points.length" class="notice">{{ store.t('noData') }}</p>
    <p v-else class="notice warn">{{ store.t('neverAground') }}</p>

    <TideChart :points="points" :extrema="[]" :seabed="result ? result.seabed : null"
               :passable="result ? result.seabed + store.settings.draught : null"
               :bands="phases" :band-label="bandLabel"
               :vessel="{ draught: store.settings.draught }" :vessel-level="boatLevel"
               :marker-time="groundingShown" @update:marker-time="setGroundingTime"
               :day-start="store.selectedDay" :day-end="dayEnd" :now="now" />
    <DateStrip />

    <h2>{{ store.t('dryingInputs') }} <InfoTip :title="store.t('dryingInputs')" :texts="[store.t('dragTheBoat'), store.t('dryingNote')]" /></h2>
    <div class="setting" :class="{ active: mode === 'time' }">
      <span>{{ store.t('groundingAt') }}</span>
      <span class="stepper measure">
        <button aria-label="−60 min" @click="stepGrounding(-60)">−60 min</button>
        <button aria-label="−10 min" @click="stepGrounding(-10)">−10 min</button>
        <span class="num value">{{ fmtTime(groundingShown) }}</span>
        <button aria-label="+10 min" @click="stepGrounding(10)">+10 min</button>
        <button aria-label="+60 min" @click="stepGrounding(60)">+60 min</button>
      </span>
    </div>
    <div class="setting" :class="{ active: mode === 'depth' }">
      <span>{{ store.t('currentWaterDepth') }}</span>
      <MeasureStepper :model-value="currentDepth" :coarse="10" :fine="1" :min="0" :max="1000" @update:model-value="setDepth" />
    </div>
    <div class="setting">
      <span>{{ store.t('tideShift') }}</span>
      <span class="stepper measure">
        <button aria-label="−30 min" @click="stepTideShift(-30)">−30 min</button>
        <button aria-label="−5 min" @click="stepTideShift(-5)">−5 min</button>
        <span class="num value">{{ tideShift > 0 ? '+' : tideShift < 0 ? '−' : '' }}{{ Math.abs(tideShift) }} min</span>
        <button aria-label="+5 min" @click="stepTideShift(5)">+5 min</button>
        <button aria-label="+30 min" @click="stepTideShift(30)">+30 min</button>
      </span>
    </div>
    <div class="setting">
      <span>{{ store.t('draughtSetting') }}</span>
      <span class="num">{{ fmtMetres(store.settings.draught) }}</span>
    </div>
    <div v-if="store.shallow && result" class="setting">
      <span class="small muted">{{ store.ownDepth ? `${store.t('myDepth')}: ${fmtMetres(store.ownDepth.depth)} ${store.t('belowDatum')} ${store.napDatumName}` : store.t('myDepthHelp') }}</span>
      <span class="stepper">
        <button v-if="store.ownDepth" class="link" @click="store.clearOwnDepth()">{{ store.t('clearMyDepth') }}</button>
        <button @click="keepSounding()">{{ store.t('saveMyDepth') }}</button>
      </span>
    </div>

  </main>
</template>
